import type { Locale } from "@/i18n/languages";
import { isEuCountry } from "@/lib/customer/vat";
import type { VatMode } from "./vat-modes";

/**
 * VAT rates in percent per country, standard rate first (as of 2026; rates change, so the editor
 * only suggests them and any rate can be typed). EU members, the neighbours Blanqo's languages
 * cover, and countries freelancers often run their business from (GB, GE, AE, AU, NZ). Where
 * zero-rating or GST-free sales are common, 0 is listed: a zero rate is a rate, not an exemption.
 */
export const VAT_RATES: Record<string, number[]> = {
  AE: [5, 0],
  AT: [20, 13, 10],
  AU: [10, 0],
  BE: [21, 12, 6],
  BG: [20, 9],
  CH: [8.1, 3.8, 2.6],
  CY: [19, 9, 5],
  CZ: [21, 12],
  DE: [19, 7],
  DK: [25],
  EE: [24, 13, 9],
  ES: [21, 10, 4],
  FI: [25.5, 13.5, 10],
  FR: [20, 10, 5.5, 2.1],
  GB: [20, 5, 0],
  GE: [18],
  GR: [24, 13, 6],
  HR: [25, 13, 5],
  HU: [27, 18, 5],
  IE: [23, 13.5, 9, 4.8],
  IS: [24, 11],
  IT: [22, 10, 5, 4],
  LI: [8.1, 3.8, 2.6],
  LT: [21, 9, 5],
  LU: [17, 14, 8, 3],
  LV: [21, 12, 5],
  MT: [18, 7, 5],
  NL: [21, 9],
  NO: [25, 15, 12],
  NZ: [15, 0],
  PL: [23, 8, 5],
  PT: [23, 13, 6],
  RO: [21, 11],
  SE: [25, 12, 6],
  SI: [22, 9.5, 5],
  SK: [23, 19, 5],
  TR: [20, 10, 1],
};

type NoteMode = Exclude<VatMode, "standard">;
type Notes = Partial<Record<NoteMode, Partial<Record<Locale, string>>>>;

/**
 * What the mandatory-field check (lib/invoice/checks.ts) asks for in a country. Without an
 * entry a country gets the EU VAT Directive's (Art. 226).
 */
export interface CheckRules {
  /** Gross amount up to which a simplified invoice is enough (no customer, no service date). */
  simplifiedUpTo?: number;
  /** A simplified invoice still names the seller's VAT number (GB, AE); in DE and AT it does not. */
  simplifiedKeepsTaxId?: boolean;
  /**
   * When the date of supply is required: always (AT), unless the invoice is simplified (DE, EU),
   * or only when it differs from the issue date (GB tax point, AE date of supply), which the
   * check cannot tell, so it never asks.
   */
  serviceDate: "always" | "unlessSimplified" | "ifDifferent";
  /**
   * VAT rules only bind businesses registered for VAT (registration starts at a turnover
   * threshold); the VAT mode "small business" means not registered.
   */
  registrationOptional?: boolean;
  /** A registered seller titles the document "Tax Invoice" (AE, AU). */
  taxInvoiceTitle?: boolean;
  /** The seller's business number is needed even without VAT registration (AU: the ABN). */
  taxIdAlways?: boolean;
  /** The customer's VAT ID above this gross amount (AT €10,000). */
  customerVatIdAbove?: number;
}

const EU_CHECKS: CheckRules = { serviceDate: "unlessSimplified" };

const CHECKS: Record<string, CheckRules> = {
  // § 14 (4) UStG; § 33 UStDV up to €250.
  DE: { simplifiedUpTo: 250, serviceDate: "unlessSimplified" },
  // § 11 (1) UStG; up to €400 simplified, the recipient's VAT ID over €10,000.
  AT: { simplifiedUpTo: 400, serviceDate: "always", customerVatIdAbove: 10_000 },
  // Art. 26 MWSTG, only when registered for VAT.
  CH: { serviceDate: "always", registrationOptional: true },
  // VAT invoice (VAT Regulations 1995, reg. 14); simplified up to £250 with the VAT number;
  // registration from £90,000 turnover.
  GB: {
    simplifiedUpTo: 250,
    simplifiedKeepsTaxId: true,
    serviceDate: "ifDifferent",
    registrationOptional: true,
  },
  // Georgia: VAT registration from GEL 100,000; most freelancers (small business status) are not.
  GE: { serviceDate: "ifDifferent", registrationOptional: true },
  // Tax invoice (Art. 59 of the Executive Regulation); simplified up to AED 10,000 with the TRN;
  // registration from AED 375,000.
  AE: {
    simplifiedUpTo: 10_000,
    simplifiedKeepsTaxId: true,
    serviceDate: "ifDifferent",
    registrationOptional: true,
    taxInvoiceTitle: true,
  },
  // Tax invoice (GST Act s 29-70): the ABN always (without it the payer withholds tax), the
  // buyer from A$1,000; GST registration from A$75,000.
  AU: {
    simplifiedUpTo: 999.99,
    simplifiedKeepsTaxId: true,
    serviceDate: "ifDifferent",
    registrationOptional: true,
    taxInvoiceTitle: true,
    taxIdAlways: true,
  },
  // Taxable supply information (GST Act 1985 s 19F, since 2023): the GST number over NZ$200, the
  // buyer over NZ$1,000; registration from NZ$60,000.
  NZ: {
    simplifiedUpTo: 1000,
    simplifiedKeepsTaxId: true,
    serviceDate: "ifDifferent",
    registrationOptional: true,
  },
};

/**
 * The no-VAT notes in a country's legal wording, by language. Countries without one get the
 * generic notes (lib/invoice/vat-notes.ts).
 */
const NOTES: Record<string, Notes> = {
  DE: {
    small_business: {
      de: "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.",
      en: "No VAT is charged under the small business scheme (§ 19 UStG).",
    },
    reverse_charge: {
      de: "Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG).",
      en: "Reverse charge: VAT is to be accounted for by the recipient (§ 13b UStG).",
    },
    intra_eu: {
      de: "Steuerfreie innergemeinschaftliche Lieferung (§ 4 Nr. 1b UStG).",
      en: "VAT-exempt intra-Community supply (§ 4 No. 1b UStG).",
    },
    export: {
      de: "Nicht im Inland steuerbare Leistung.",
      en: "Not taxable in Germany: the place of supply is abroad.",
    },
    exempt: {
      de: "Umsatzsteuerfreie Leistung gemäß § 4 UStG.",
      en: "VAT-exempt supply (§ 4 UStG).",
    },
  },
  AT: {
    small_business: {
      de: "Umsatzsteuerbefreit – Kleinunternehmer gemäß § 6 Abs. 1 Z 27 UStG.",
      en: "VAT-exempt small business (§ 6 (1) 27 UStG).",
    },
    reverse_charge: {
      de: "Übergang der Steuerschuld auf den Leistungsempfänger (Reverse Charge).",
      en: "Reverse charge: VAT is to be accounted for by the recipient.",
    },
    intra_eu: {
      de: "Steuerfreie innergemeinschaftliche Lieferung (Art. 6 Abs. 1 UStG).",
      en: "VAT-exempt intra-Community supply (Art. 6 (1) UStG).",
    },
    export: {
      de: "Nicht im Inland steuerbare Leistung.",
      en: "Not taxable in Austria: the place of supply is abroad.",
    },
    exempt: {
      de: "Umsatzsteuerbefreit gemäß § 6 UStG.",
      en: "VAT-exempt supply (§ 6 UStG).",
    },
  },
  CH: {
    small_business: {
      de: "Nicht mehrwertsteuerpflichtig.",
      en: "Not registered for VAT.",
    },
    reverse_charge: {
      de: "Bezugsteuer: Die Mehrwertsteuer schuldet der Leistungsempfänger.",
      en: "Reverse charge: VAT is owed by the recipient.",
    },
    export: {
      de: "Leistung im Ausland, von der Mehrwertsteuer befreit.",
      en: "Supply abroad, exempt from Swiss VAT.",
    },
    exempt: {
      de: "Von der Mehrwertsteuer ausgenommene Leistung.",
      en: "Supply excluded from Swiss VAT.",
    },
  },
  GB: {
    small_business: {
      en: "Not registered for VAT.",
      de: "Nicht für die Umsatzsteuer registriert (VAT).",
    },
    reverse_charge: {
      en: "Reverse charge: the customer accounts for the VAT to HMRC.",
      de: "Reverse Charge: Die Umsatzsteuer führt der Leistungsempfänger ab.",
    },
    intra_eu: {
      en: "Outside the scope of UK VAT: the place of supply is in the EU.",
      de: "Nicht im Vereinigten Königreich steuerbar: Leistungsort in der EU.",
    },
    export: {
      en: "Export of services: outside the scope of UK VAT.",
      de: "Leistung ins Ausland: nicht im Vereinigten Königreich steuerbar.",
    },
    exempt: {
      en: "Exempt from VAT.",
      de: "Von der Umsatzsteuer befreit (VAT exempt).",
    },
  },
  GE: {
    small_business: {
      en: "Not registered for VAT in Georgia.",
      de: "In Georgien nicht für die Umsatzsteuer registriert.",
    },
    reverse_charge: {
      en: "Reverse charge: VAT is to be accounted for by the recipient.",
      de: "Reverse Charge: Die Umsatzsteuer schuldet der Leistungsempfänger.",
    },
    export: {
      en: "No VAT: the place of supply is outside Georgia.",
      de: "Keine Umsatzsteuer: Der Leistungsort liegt außerhalb Georgiens.",
    },
    exempt: {
      en: "Exempt from VAT under the Tax Code of Georgia.",
      de: "Umsatzsteuerfrei nach dem georgischen Steuergesetzbuch.",
    },
  },
  AE: {
    small_business: {
      en: "Not registered for VAT in the UAE.",
      de: "In den VAE nicht für die Umsatzsteuer registriert.",
    },
    reverse_charge: {
      en: "Reverse charge: the recipient accounts for the VAT (Art. 48, Federal Decree-Law No. 8 of 2017).",
      de: "Reverse Charge: Die Umsatzsteuer schuldet der Leistungsempfänger (Art. 48 Bundesgesetzesdekret Nr. 8/2017).",
    },
    export: {
      en: "Zero-rated export of services (Art. 31, Federal Decree-Law No. 8 of 2017).",
      de: "Ausfuhr von Dienstleistungen zum Nullsatz (Art. 31 Bundesgesetzesdekret Nr. 8/2017).",
    },
    exempt: {
      en: "Exempt supply (Art. 46, Federal Decree-Law No. 8 of 2017).",
      de: "Steuerfreie Leistung (Art. 46 Bundesgesetzesdekret Nr. 8/2017).",
    },
  },
  AU: {
    small_business: {
      en: "Not registered for GST.",
      de: "Nicht für die GST registriert.",
    },
    reverse_charge: {
      en: "Reverse charge: the recipient accounts for the GST.",
      de: "Reverse Charge: Die GST schuldet der Leistungsempfänger.",
    },
    export: {
      en: "GST-free export (A New Tax System (Goods and Services Tax) Act 1999, s 38-190).",
      de: "GST-freie Ausfuhr (GST Act 1999, s 38-190).",
    },
    exempt: {
      en: "Input-taxed supply: no GST.",
      de: "Input-taxed: keine GST.",
    },
  },
  NZ: {
    small_business: {
      en: "Not registered for GST.",
      de: "Nicht für die GST registriert.",
    },
    reverse_charge: {
      en: "Reverse charge: the recipient accounts for the GST.",
      de: "Reverse Charge: Die GST schuldet der Leistungsempfänger.",
    },
    export: {
      en: "Zero-rated: services supplied to a non-resident (Goods and Services Tax Act 1985, s 11A).",
      de: "Nullsatz: Leistung an einen Nichtansässigen (GST Act 1985, s 11A).",
    },
    exempt: {
      en: "Exempt supply (Goods and Services Tax Act 1985, s 14).",
      de: "Steuerfreie Leistung (GST Act 1985, s 14).",
    },
  },
  MT: {
    small_business: {
      en: "Exempt without credit: small undertaking under Article 11 of the VAT Act (Cap. 406).",
      de: "Ohne Vorsteuerabzug befreit: Kleinunternehmen nach Artikel 11 des maltesischen MwSt-Gesetzes.",
    },
  },
  NL: {
    small_business: {
      nl: "Vrijgesteld van btw op grond van de kleineondernemersregeling (KOR).",
      en: "VAT-exempt under the Dutch small business scheme (KOR).",
      de: "Umsatzsteuerfrei nach der niederländischen Kleinunternehmerregelung (KOR).",
    },
  },
};

/** Countries whose notes are in their legal wording (isPresetVatNote looks through them). */
export const NOTE_COUNTRIES = Object.keys(NOTES);

/**
 * Reasons for exempt lines where the seller country has no preset: the VAT Directive's articles
 * (2006/112/EC). Like the notes only a starting point; the exact wording is the user's call.
 */
const EU_EXEMPTIONS: Partial<Record<Locale, string[]>> = {
  en: [
    "VAT-exempt under Art. 132(1)(c) of Directive 2006/112/EC (medical care)",
    "VAT-exempt under Art. 132(1)(i) and (j) of Directive 2006/112/EC (education)",
    "VAT-exempt under Art. 135(1)(l) of Directive 2006/112/EC (letting of immovable property)",
    "VAT-exempt under Art. 135(1) of Directive 2006/112/EC (financial services)",
  ],
  de: [
    "Steuerfrei gemäß Art. 132 Abs. 1 Buchst. c MwStSystRL (Heilbehandlung)",
    "Steuerfrei gemäß Art. 132 Abs. 1 Buchst. i und j MwStSystRL (Unterricht)",
    "Steuerfrei gemäß Art. 135 Abs. 1 Buchst. l MwStSystRL (Vermietung von Grundstücken)",
    "Steuerfrei gemäß Art. 135 Abs. 1 MwStSystRL (Finanzumsätze)",
  ],
};

/** Common reasons a line is exempt from VAT, in a country's legal wording, by language. */
const EXEMPTIONS: Record<string, Partial<Record<Locale, string[]>>> = {
  DE: {
    de: [
      "Steuerfrei nach § 4 Nr. 14 Buchst. a UStG (Heilbehandlung)",
      "Steuerfrei nach § 4 Nr. 21 UStG (Unterrichtsleistung)",
      "Steuerfrei nach § 4 Nr. 12 UStG (Vermietung von Grundstücken)",
      "Steuerfrei nach § 4 Nr. 8 UStG (Finanzumsätze)",
      "Steuerfrei nach § 4 Nr. 11 UStG (Versicherungsvermittlung)",
      "Steuerfrei nach § 4 Nr. 20 UStG (kulturelle Leistung)",
    ],
    en: [
      "VAT-exempt under § 4 No. 14(a) UStG (medical treatment)",
      "VAT-exempt under § 4 No. 21 UStG (teaching)",
      "VAT-exempt under § 4 No. 12 UStG (letting of property)",
      "VAT-exempt under § 4 No. 8 UStG (financial services)",
      "VAT-exempt under § 4 No. 11 UStG (insurance brokerage)",
      "VAT-exempt under § 4 No. 20 UStG (cultural services)",
    ],
  },
  AT: {
    de: [
      "Steuerfrei gemäß § 6 Abs. 1 Z 19 UStG (Heilbehandlung)",
      "Steuerfrei gemäß § 6 Abs. 1 Z 11 UStG (Unterricht)",
      "Steuerfrei gemäß § 6 Abs. 1 Z 16 UStG (Vermietung von Grundstücken)",
      "Steuerfrei gemäß § 6 Abs. 1 Z 8 UStG (Finanzumsätze)",
    ],
    en: [
      "VAT-exempt under § 6 (1) 19 UStG (medical treatment)",
      "VAT-exempt under § 6 (1) 11 UStG (teaching)",
      "VAT-exempt under § 6 (1) 16 UStG (letting of property)",
      "VAT-exempt under § 6 (1) 8 UStG (financial services)",
    ],
  },
  GB: {
    en: [
      "Exempt from VAT: VATA 1994, Schedule 9, Group 6 (education)",
      "Exempt from VAT: VATA 1994, Schedule 9, Group 7 (health and welfare)",
      "Exempt from VAT: VATA 1994, Schedule 9, Group 1 (land)",
      "Exempt from VAT: VATA 1994, Schedule 9, Group 5 (finance)",
      "Exempt from VAT: VATA 1994, Schedule 9, Group 2 (insurance)",
    ],
  },
  AE: {
    en: [
      "Exempt supply: financial services (Art. 46, Federal Decree-Law No. 8 of 2017)",
      "Exempt supply: residential property (Art. 46, Federal Decree-Law No. 8 of 2017)",
      "Exempt supply: local passenger transport (Art. 46, Federal Decree-Law No. 8 of 2017)",
    ],
  },
};

/** What new invoices take from a country outside the euro area: currency, dates, numbers. */
export type LocalDefaults = { currency: string; dateFormat: string; currencyFormat?: string };

/**
 * The local currency, date and number format of new invoices in countries outside the euro area
 * (new workspaces start with German formats); applyCountry() (lib/api/company.ts) sets them with
 * the country. The US and Canada have no VAT preset yet (#92) but their formats.
 */
const LOCAL: Record<string, LocalDefaults> = {
  GB: { currency: "GBP", dateFormat: "DD/MM/YYYY", currencyFormat: "en-US" },
  AE: { currency: "AED", dateFormat: "DD/MM/YYYY", currencyFormat: "en-US" },
  GE: { currency: "GEL", dateFormat: "DD.MM.YYYY" },
  AU: { currency: "AUD", dateFormat: "DD/MM/YYYY", currencyFormat: "en-US" },
  NZ: { currency: "NZD", dateFormat: "DD/MM/YYYY", currencyFormat: "en-US" },
  US: { currency: "USD", dateFormat: "MM/DD/YYYY", currencyFormat: "en-US" },
  CA: { currency: "CAD", dateFormat: "YYYY-MM-DD", currencyFormat: "en-CA" },
};

export const localDefaults = (country: string | null | undefined): LocalDefaults | null =>
  (country && LOCAL[country]) || null;

/** North American wording on English documents (US_LABELS, US email text): US and Canada. */
export const NORTH_AMERICAN = ["US", "CA"];

export interface CountryPreset {
  code: string;
  /** Standard rate first. */
  rates: number[];
  checks: CheckRules;
  /** The no-VAT notes in this country's legal wording, by language. */
  notes: Notes;
  /** Common reasons a line is exempt from VAT, in this country's legal wording, by language. */
  exemptions?: Partial<Record<Locale, string[]>>;
  /** The local currency, date and number format of new invoices (outside the euro area). */
  local?: LocalDefaults;
}

/** The preset of a country: rates for every listed one, own rules and wording for some. */
export function countryPreset(code: string | null | undefined): CountryPreset | null {
  if (!code) return null;
  const rates = VAT_RATES[code];
  if (!rates) return null;
  return {
    code,
    rates,
    checks: CHECKS[code] ?? EU_CHECKS,
    notes: NOTES[code] ?? {},
    ...(EXEMPTIONS[code] && { exemptions: EXEMPTIONS[code] }),
    ...(LOCAL[code] && { local: LOCAL[code] }),
  };
}

/**
 * Suggested reasons for exempt lines in `language`: the seller country's legal wording (DE, AT,
 * GB, AE), else the VAT Directive's for EU members and a seller without a country; English where
 * a language has none. Sellers outside the EU (CH, NO, …) get none: the directive is not their law.
 */
export function exemptionReasons(country: string | null | undefined, language: Locale): string[] {
  const own = country ? EXEMPTIONS[country] : undefined;
  const list = own ?? (!country || isEuCountry(country) ? EU_EXEMPTIONS : {});
  return list[language] ?? list.en ?? [];
}
